import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, Clock, ChevronRight, Sparkles, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { blogCategories, blogArticles as fallbackArticles } from '../data/blog';
import BlogArticleModal from './BlogArticleModal';
import api from '../api/client';

const HOMEPAGE_LIMIT = 6;

const BlogGuides = () => {
  const [active, setActive] = useState('all');
  const [open, setOpen] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Only fetch the 6 most recent so the homepage stays snappy.
        const list = await api.listBlogs({ limit: HOMEPAGE_LIMIT });
        setArticles(list && list.length ? list : fallbackArticles.slice(0, HOMEPAGE_LIMIT));
      } catch (_e) {
        setArticles(fallbackArticles.slice(0, HOMEPAGE_LIMIT));
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const cat = blogCategories.find((c) => c.id === active);
    if (!cat) return articles;
    return articles.filter(cat.match);
  }, [active, articles]);

  return (
    <section id="blog-guides" className="scroll-mt-24 py-12 sm:py-20" style={{ backgroundColor: '#F4F9F5' }}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest" style={{ color: '#1E563E' }}>
            <BookOpen className="w-3.5 h-3.5" /> Blog &amp; Guides
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mt-1" style={{ color: '#0d3d29' }}>Guided Reading for a Calmer Mind</h2>
          <p className="mt-3 text-emerald-900/70 max-w-2xl mx-auto">Short, practical guides paired with a matching audio meditation — a peaceful five-minute read to help you understand and use each practice.</p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {blogCategories.map((c) => {
            const isActive = active === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                className={`text-xs px-3.5 py-1.5 rounded-full border transition-colors ${isActive ? 'text-white' : 'text-emerald-800 bg-white hover:bg-emerald-50 border-emerald-200'}`}
                style={isActive ? { backgroundColor: '#1E563E', borderColor: '#1E563E' } : {}}
              >
                {c.id === 'sleep' && '🌙 '}
                {c.id === 'stress' && '🌿 '}
                {c.id === 'money' && '✨ '}
                {c.id === 'hindi' && '🇮🇳 '}
                {c.label}
              </button>
            );
          })}
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-emerald-200 bg-white/60 p-10 text-center">
            <h3 className="font-serif text-2xl text-emerald-900">No guides in this category yet.</h3>
            <p className="text-sm text-emerald-800/80 mt-1">Try another filter — more coming soon.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((a) => (
              <article
                key={a.id}
                onClick={() => setOpen(a)}
                className="group cursor-pointer rounded-2xl overflow-hidden border border-emerald-100 bg-white hover:shadow-xl hover:shadow-emerald-900/10 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="relative aspect-[16/10]">
                  <img src={a.image} alt={a.title} onError={(e) => { e.currentTarget.style.display = 'none'; }} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                  <span className="absolute top-3 left-3 rounded-full bg-white/90 backdrop-blur px-2.5 py-0.5 text-[10px] font-medium" style={{ color: '#1E563E' }}>
                    {a.categoryEmoji} {a.category}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-xl leading-tight line-clamp-2" style={{ color: '#0d3d29' }}>{a.title}</h3>
                  <p className="mt-2 text-sm text-emerald-900/75 line-clamp-3">{a.excerpt}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-emerald-800">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {a.readMinutes}-min read</span>
                    <span className="inline-flex items-center gap-1 font-medium" style={{ color: '#1E563E' }}>
                      <Sparkles className="w-3.5 h-3.5" /> Read Guide <ChevronRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="mt-10 flex justify-center">
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 rounded-full text-white text-sm px-5 py-2.5 hover:opacity-90 shadow-md"
            style={{ backgroundColor: '#1E563E' }}
          >
            View All Blogs &amp; Guides <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {open && <BlogArticleModal article={open} onClose={() => setOpen(null)} />}
    </section>
  );
};

export default BlogGuides;
